import Phaser from 'phaser';
import '@fontsource/barlow-condensed/500.css';
import '@fontsource/barlow-condensed/600.css';
import '@fontsource/barlow-condensed/700.css';
import '@fontsource/anton/400.css';
import './styles/main.css';
import './styles/theme.css';
import { GAME_CONFIG } from './config/gameConfig';
import { applyRuntimeOverrides } from './config/runtimeConfig';
import { ActionBus } from './input/actions';
import { GameScene } from './scenes/GameScene';
import { Hud } from './ui/Hud';
import { ECONOMY } from './sim/buildings/definitions';
import { modeSetup } from './sim/match';
import { MODES } from './sim/modes/modes';
import { levelMode, levelObjectives, resolveLevel } from './sim/campaign/levels';
import './sim/campaign/weekly';
import { Simulation } from './sim/Simulation';
import { RecordingSink, clearSave, loadSave, matchQuery, replay } from './meta/savegame';
import { generateMap } from './world/mapGenerator';
import { NetSession } from './net/NetSession';
import { Lobby } from './ui/Lobby';
import { SAMPLE_BASES, validateBase, type BaseLayout } from './sim/raid/bases';
import { loadProfile } from './meta/profile';
import { ensureServerProfile, roomServer } from './net/api';
import { MainMenu } from './ui/MainMenu';
import { tr } from './i18n/i18n';
import { loadSettings } from './meta/settings';
import { GATED, unlocksFor } from './sim/content/pass';
import { cachedPremium } from './meta/entitlements';
import type { PlayerId } from './sim/ids';

/** An online match handed to `boot` by the lobby. */
interface OnlineMatch {
  readonly sim: Simulation;
  readonly me: PlayerId;
  readonly net: NetSession;
}

/** Offline unlocks from the local profile's XP (and the last premium status the server confirmed). */
function localUnlocks(): string[] {
  return unlocksFor(loadProfile().xp, cachedPremium());
}

/** Tracks live game instances so tests can prove no duplicate game loop exists. */
let liveGames = 0;

function boot(online?: OnlineMatch): { game: Phaser.Game; hud: Hud; bus: ActionBus } {
  const parent = document.getElementById('game');
  const hudRoot = document.getElementById('hud');
  if (!parent || !hudRoot) throw new Error('Missing #game or #hud container');

  const { config, warnings } = applyRuntimeOverrides(GAME_CONFIG, window.location.search);
  for (const w of warnings) console.warn(`[After the Fall] ${w}`);

  // Online: the lobby built the Simulation from the server's start message (same seed / map as everyone).
  const level = online ? null : resolveLevel(config.colony);
  const mode = online ? online.sim.mode : level ? levelMode(level) : MODES[config.colony.mode ?? 'sandbox'];
  const map = online ? online.sim.world : generateMap(config.map);
  // Offline Endless Survival is you alone against the hordes unless the URL asks for more colonies.
  const soloSurvival = !online && mode.id === 'last_colony' && !new URLSearchParams(window.location.search).has('players');
  const seats = soloSurvival ? 1 : Math.max(mode.players.min, Math.min(mode.players.max, config.colony.players));
  // Offline raid (Phase 13): the defender is a saved base — a sample, or your own (`?base=0|1|2|mine`).
  const raidBase = !online && mode.id === 'raid' ? pickRaidBase(new URLSearchParams(window.location.search).get('base')) : null;
  // Offline rivals in Skirmish / Duel / multi-colony Last Colony are played by the computer (D115); the sandbox rival stays silent.
  const aiParam = new URLSearchParams(window.location.search).get('ai');
  const ai = aiParam === 'off' || mode.id === 'sandbox' || mode.id === 'raid' || mode.id === 'campaign' ? undefined : aiParam === 'easy' || aiParam === 'hard' ? aiParam : 'normal';
  const plain = modeSetup(seats, mode.teams, false, ai);
  const setup = raidBase ? { ...plain, players: plain.players.map((p, i) => (i === 1 ? { ...p, name: raidBase.name } : p)) } : plain;
  const sim = online ? online.sim : new Simulation(map, setup, {
    mode,
    buildRadius: config.colony.buildRadius,
    startingResources: { ...ECONOMY.startingResources, ...mode.startingResources, ...config.colony.startingResources },
    startingSurvivors: config.colony.startingSurvivors ?? mode.startingSurvivors,
    startingUnits: config.colony.startingUnits ?? level?.startingUnits,
    startingEnemies: config.colony.startingEnemies,
    ...(raidBase ? { raid: { base: raidBase } } : {}),
    ...(level && levelObjectives(level) ? { objectives: levelObjectives(level) } : {}),
    // Season pass (D112): the local player gets what their profile has earned; computer-held colonies get everything.
    unlocks: Object.fromEntries(setup.players.map((p) => [p.id, p.control === 'local' ? localUnlocks() : [...GATED]])),
    infected: {
      ...mode.infected,
      seed: config.map.seed,
      ...(config.colony.startingInfected !== undefined ? { initial: config.colony.startingInfected } : {}),
      ...(config.colony.waves !== undefined ? { waves: config.colony.waves } : {}),
      // D127: offline Endless Survival on good devices grows into real swarms (up to 1500 infected).
      ...(soloSurvival ? (loadSettings().quality === 'high' ? { maxInfected: 1500, maxWaveSize: 260 } : { maxInfected: 500, maxWaveSize: 90 }) : {}),
    },
  });
  const localPlayer = online ? online.me : setup.players.find((p) => p.control === 'local')!.id;

  // Offline save / resume (D118): every accepted command is recorded; `?…&resume=1` replays the saved log first.
  const recorder = online ? null : new RecordingSink(sim);
  if (recorder) {
    const query = matchQuery(window.location.search);
    const save = new URLSearchParams(window.location.search).has('resume') ? loadSave() : null;
    if (save && save.query === query && !replay(sim, save, recorder)) console.warn('[After the Fall] the save did not replay cleanly');
    const label = level ? `Level ${level.id} · ${level.name}` : mode.name;
    const write = (): void => {
      recorder.save(query, label);
    };
    const timer = window.setInterval(write, 15_000);
    const onHide = (): void => {
      if (document.visibilityState === 'hidden') write();
    };
    window.addEventListener('pagehide', write);
    document.addEventListener('visibilitychange', onHide);
    sim.match.subscribe((e) => e.type === 'ended' && clearSave());
    if (import.meta.hot) {
      import.meta.hot.dispose(() => {
        window.clearInterval(timer);
        window.removeEventListener('pagehide', write);
        document.removeEventListener('visibilitychange', onHide);
      });
    }
  }
  const bus = new ActionBus();
  // Touch-first devices (phones, tablets) start in touch mode; the HUD switches live if the other input is used.
  const touchFirst = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  const hud = new Hud(hudRoot, bus, {
    title: config.title,
    phaseLabel: online ? `${tr(mode.name)} · Online` : config.colony.weekly ? `${tr('Weekly Challenge')} · ${config.colony.weekly}` : level ? `${tr('Level {n}', { n: level.id })} · ${tr(level.name)}` : tr(mode.name),
    buttonZoomStep: config.camera.buttonZoomStep,
    inputMode: touchFirst ? 'touch' : 'mouse',
  });
  document.title = config.title;

  const game = new Phaser.Game({
    type: Phaser.AUTO,
    parent,
    backgroundColor: '#15171a',
    scale: {
      mode: Phaser.Scale.RESIZE,
      width: parent.clientWidth || window.innerWidth,
      height: parent.clientHeight || window.innerHeight,
    },
    render: { antialias: true, pixelArt: false, roundPixels: false },
    // Keyboard is read natively by KeyboardInput (see DECISIONS D16), so Phaser's plugin is off.
    // 3 touch pointers: two for pinch plus one spare so a stray third finger never steals one.
    input: { mouse: { preventDefaultWheel: true }, touch: { capture: true }, activePointers: 3, keyboard: false },
    disableContextMenu: true,
    banner: false,
    scene: [new GameScene({ config, map, bus, hud, sim, sink: online ? online.net.sink : recorder!, localPlayer, liveGames: () => liveGames, net: online?.net })],
  });
  liveGames++;
  game.events.once(Phaser.Core.Events.DESTROY, () => liveGames--);

  // Phaser 3.90 bug: on an orientation change it refreshes with the OLD parent size,
  // then silently records the new size without applying it, so the canvas keeps the
  // previous orientation's dimensions until the next resize. Watching the container
  // directly (fires after layout) and re-reading its bounds before refreshing fixes it.
  const sizeWatch = typeof ResizeObserver === 'function' ? new ResizeObserver(() => {
    const scale = game.scale;
    const r = parent.getBoundingClientRect();
    if (Math.round(r.width) !== Math.round(scale.gameSize.width) || Math.round(r.height) !== Math.round(scale.gameSize.height)) {
      scale.getParentBounds();
      scale.refresh();
    }
  }) : null;
  sizeWatch?.observe(parent);
  game.events.once(Phaser.Core.Events.DESTROY, () => sizeWatch?.disconnect());
  return { game, hud, bus };
}

/**
 * Stop the BROWSER's own gestures (page pinch-zoom, double-tap zoom, iOS Safari
 * gesture events) so pinch zooms the map, never the page. CSS `touch-action`
 * does most of this; iOS Safari also needs its proprietary gesture events and
 * multi-finger touchmove cancelled. Returns a cleanup for HMR.
 */
function blockBrowserGestures(): () => void {
  const stop = (e: Event): void => e.preventDefault();
  const stopMulti = (e: TouchEvent): void => {
    if (e.touches.length > 1) e.preventDefault();
  };
  const opts: AddEventListenerOptions = { passive: false };
  for (const t of ['gesturestart', 'gesturechange', 'gestureend']) document.addEventListener(t, stop, opts);
  document.addEventListener('touchmove', stopMulti, opts);
  return () => {
    for (const t of ['gesturestart', 'gesturechange', 'gestureend']) document.removeEventListener(t, stop);
    document.removeEventListener('touchmove', stopMulti);
  };
}

function pickRaidBase(param: string | null): BaseLayout {
  if (param === 'mine') {
    const mine = loadProfile().base;
    if (mine && validateBase(mine) === null) return mine;
  }
  const i = Number(param);
  return SAMPLE_BASES[Number.isInteger(i) && i >= 0 && i < SAMPLE_BASES.length ? i : 0]!;
}

/** `?room=CODE` (or `?room=new`): lobby first, the game boots when the server starts the match. */
function runOnline(roomParam: string): { game: Phaser.Game; hud: Hud; bus: ActionBus } | null {
  const server = roomServer();
  const host = document.getElementById('hud')!;
  if (!server) {
    host.innerHTML = '<section class="lobby"><div class="lobby-card"><h1 class="lobby-title">After the Fall</h1><p data-testid="lobby-error">Online play needs the room server, which is not deployed yet. <a href="./">Play offline</a></p></div></section>';
    return null;
  }
  let code = roomParam.toUpperCase();
  const go = (): void => {
    let name = 'Survivor';
    try {
      name = localStorage.getItem('atf-name') ?? name;
    } catch {
      /* storage blocked */
    }
    const nameParam = new URLSearchParams(window.location.search).get('name');
    if (nameParam) name = nameParam.slice(0, 20);
    let lobby: Lobby | null = null;
    let app: { game: Phaser.Game; hud: Hud; bus: ActionBus } | null = null;
    const net = new NetSession(server, code, name, {
      onLobby: (seats, options) => {
        if (net.me) lobby?.setMe(net.me);
        lobby?.update(seats, options);
      },
      onStart: (sim, me) => {
        lobby?.destroy();
        lobby = null;
        app = boot({ sim, me, net });
      },
      onReject: (m) => app?.hud.toast(m, 'error'),
      onError: (_c, m) => (app ? app.hud.toast(m, 'error') : lobby?.setStatus(m)),
      onStatus: (st) => {
        if (st === 'reconnecting') (app ? app.hud.toast('Connection lost — reconnecting…', 'error') : lobby?.setStatus('Reconnecting…'));
        if (st === 'online' && app) app.hud.toast('Connected.', 'ok');
      },
      onDesync: () => {
        app?.hud.toast('Out of sync with the server — rejoining…', 'error');
        setTimeout(() => window.location.reload(), 1500);
      },
    });
    const raidTarget = new URLSearchParams(window.location.search).get('raid');
    lobby = new Lobby(host, code, {
      ready: (on) => net.setReady(on),
      options: (o) => net.setOptions(devOptions(o)),
      start: () => net.startMatch(),
      leave: () => {
        net.leave();
        window.location.href = window.location.pathname;
      },
    });
    (window as unknown as { __ATF_NET__?: NetSession }).__ATF_NET__ = net;
    // Online raid (Phase 13): a one-player room against a saved base; it starts as soon as we are in.
    if (raidTarget && /^[0-9a-f]{16}$/.test(raidTarget)) {
      net.afterWelcome = () => {
        net.setOptions({ mode: 'raid', teams: 'ffa', defender: raidTarget });
        setTimeout(() => net.startMatch(), 300);
      };
    }
    void ensureServerProfile(server).then((p) => {
      net.profile = p.server;
      net.connect();
    });
  };
  if (code === 'NEW') {
    fetch(`${server.replace(/^ws/, 'http').replace(/\/$/, '')}/rooms/new`)
      .then((r) => r.json() as Promise<{ code: string }>)
      .then((r) => {
        code = r.code;
        const u = new URL(window.location.href);
        u.searchParams.set('room', code);
        window.history.replaceState(null, '', u.toString());
        go();
      })
      .catch(() => (host.innerHTML = '<section class="lobby"><div class="lobby-card"><p data-testid="lobby-error">Could not reach the room server. <a href="./">Play offline</a></p></div></section>'));
  } else if (/^[A-Z]{5}$/.test(code)) {
    go();
  } else {
    host.innerHTML = '<section class="lobby"><div class="lobby-card"><p data-testid="lobby-error">That room code is not valid. <a href="./">Play offline</a></p></div></section>';
  }
  return null;
}

/** Test rooms: `?dev=units:24,speed:5,zombies:0,waves:0` is passed to the room's options (the server clamps it). */
function devOptions(o: import('./net/protocol').RoomOptions): import('./net/protocol').RoomOptions {
  const d = new URLSearchParams(window.location.search).get('dev');
  if (!d) return o;
  const dev: Record<string, number | boolean> = {};
  for (const part of d.split(',')) {
    const [k, v] = part.split(':');
    if (k === 'waves') dev.waves = v === '1';
    else if (k && ['units', 'speed', 'zombies'].includes(k) && Number.isFinite(Number(v))) dev[k] = Number(v);
  }
  return { ...o, dev };
}

const unblockGestures = blockBrowserGestures();
const roomParam = new URLSearchParams(window.location.search).get('room');
// A bare URL opens the main menu (Phase 16); every menu choice is a URL with parameters, which boots straight in.
const gameParams = new URLSearchParams(window.location.search);
gameParams.delete('server');
const menu = !roomParam && !gameParams.toString() ? new MainMenu(document.getElementById('hud')!) : null;
const app = menu ? null : roomParam ? runOnline(roomParam) : boot();

// Vite HMR: tear down the old instance (game loop, input, HUD listeners) before the new one boots.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    app?.game.destroy(true);
    app?.hud.destroy();
    app?.bus.clear();
    menu?.destroy();
    unblockGestures();
  });
}

// Installable app + offline shell (Phase 19, D107). Production builds only; never during automated tests.
if (import.meta.env.PROD && 'serviceWorker' in navigator && !navigator.webdriver) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((e: unknown) => console.warn('[After the Fall] service worker not registered', e));
  });
}

